import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, X, Loader2, Star } from 'lucide-react';
import api, { resolveMedia } from '../lib/api';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { INDUSTRIES } from '../components/CaseStudies';

const STATUS_OPTIONS = ['draft', 'published'];

const emptyForm = {
  title: '',
  industry: 'Financial Services',
  slug: '',
  client: '',
  challenge: '',
  solution: '',
  outcome: '',
  metrics: [],
  tags: [],
  thumbnail: null,
  featured: false,
  status: 'draft'
};

const AdminCaseStudyFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState(emptyForm);
  const [loaded, setLoaded] = useState(!isEdit);
  const [tagInput, setTagInput] = useState('');
  const [metricInput, setMetricInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/case-studies/${id}`).then((r) => {
      setForm({
        ...emptyForm,
        ...r.data,
        tags: r.data.tags || [],
        metrics: r.data.metrics || []
      });
    }).catch(() => toast({ title: 'Failed to load case study' })).finally(() => setLoaded(true));
  }, [id, isEdit, toast]);

  const addToList = (key, value, setValue) => {
    const t = value.trim();
    if (!t || form[key].includes(t)) { setValue(''); return; }
    setForm((f) => ({ ...f, [key]: [...f[key], t] }));
    setValue('');
  };

  const removeFromList = (key, item) => setForm({ ...form, [key]: form[key].filter((x) => x !== item) });

  const onThumbnail = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, thumbnail: data.url }));
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast({ title: 'Title is required' }); return; }
    if (!form.industry) { toast({ title: 'Pick an industry' }); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/case-studies/${id}`, form);
        toast({ title: 'Case study updated' });
      } else {
        await api.post('/case-studies', form);
        toast({ title: 'Case study created' });
      }
      navigate('/admin/case-studies');
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05080f]">
      <header className="border-b border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/admin/case-studies" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Back to case studies
          </Link>
          <div className="text-white font-display text-lg">{isEdit ? 'Edit case study' : 'New case study'}</div>
        </div>
      </header>

      <form onSubmit={submit} className="max-w-4xl mx-auto px-6 py-10 space-y-6" data-testid="case-study-form">
        {!loaded && (
          <div className="card-dark rounded-3xl p-10 text-center text-slate-400 text-sm inline-flex items-center gap-2 justify-center w-full">
            <Loader2 className="animate-spin" size={14} /> Loading case study…
          </div>
        )}
        {loaded && (
        <>
        <div className="card-dark rounded-3xl p-6 md:p-8 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs text-slate-400">Title *</label>
              <Input
                data-testid="cs-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-2 bg-white/[0.03] border-white/10 text-white h-11"
                required
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">Client (optional)</label>
              <Input
                value={form.client}
                onChange={(e) => setForm({ ...form, client: e.target.value })}
                className="mt-2 bg-white/[0.03] border-white/10 text-white h-11"
                placeholder="Confidential, ACME Corp, ..."
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs text-slate-400">Industry *</label>
              <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                <SelectTrigger data-testid="cs-industry" className="mt-2 bg-white/[0.03] border-white/10 text-white h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0b1220] border-white/10 text-white">
                  {INDUSTRIES.map((i) => <SelectItem key={i.name} value={i.name}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-2 bg-white/[0.03] border-white/10 text-white h-11 capitalize">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0b1220] border-white/10 text-white capitalize">
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400">Challenge</label>
            <Textarea
              rows={3}
              value={form.challenge}
              onChange={(e) => setForm({ ...form, challenge: e.target.value })}
              className="mt-2 bg-white/[0.03] border-white/10 text-white"
              placeholder="What problem did the customer have?"
            />
          </div>

          <div>
            <label className="text-xs text-slate-400">Solution</label>
            <Textarea
              rows={3}
              value={form.solution}
              onChange={(e) => setForm({ ...form, solution: e.target.value })}
              className="mt-2 bg-white/[0.03] border-white/10 text-white"
              placeholder="What did we build or deploy?"
            />
          </div>

          <div>
            <label className="text-xs text-slate-400">Outcome</label>
            <Textarea
              rows={3}
              value={form.outcome}
              onChange={(e) => setForm({ ...form, outcome: e.target.value })}
              className="mt-2 bg-white/[0.03] border-white/10 text-white"
              placeholder="Business impact — what got better?"
            />
          </div>

          <div>
            <label className="text-xs text-slate-400">Metrics (e.g. &quot;40% cost reduction&quot;)</label>
            <div className="mt-2 flex gap-2">
              <Input
                value={metricInput}
                onChange={(e) => setMetricInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToList('metrics', metricInput, setMetricInput); } }}
                className="bg-white/[0.03] border-white/10 text-white h-11"
                placeholder="Type and press Enter"
              />
              <Button type="button" onClick={() => addToList('metrics', metricInput, setMetricInput)} variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10 h-11">Add</Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {form.metrics.map((m) => (
                <span key={m} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-200 border border-amber-500/30">
                  {m}
                  <button type="button" onClick={() => removeFromList('metrics', m)} className="hover:text-white">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400">Tags</label>
            <div className="mt-2 flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToList('tags', tagInput, setTagInput); } }}
                className="bg-white/[0.03] border-white/10 text-white h-11"
                placeholder="Type and press Enter"
              />
              <Button type="button" onClick={() => addToList('tags', tagInput, setTagInput)} variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10 h-11">Add</Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {form.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-white/5 text-slate-300 border border-white/10">
                  {t}
                  <button type="button" onClick={() => removeFromList('tags', t)} className="hover:text-white">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400">URL slug (optional)</label>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-slate-500 shrink-0 hidden sm:inline">/case-studies/</span>
              <Input
                data-testid="cs-slug"
                value={form.slug || ''}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="bg-white/[0.03] border-white/10 text-white h-11"
                placeholder="auto-generated from title"
              />
              {isEdit && form.slug && (
                <a
                  href={`/case-studies/${form.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-amber-300 hover:text-amber-200 shrink-0 whitespace-nowrap"
                  data-testid="cs-view-live"
                >
                  View live ↗
                </a>
              )}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">Leave blank on create to auto-generate from the title.</p>
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(e) => setForm({ ...form, featured: e.target.checked })}
              className="h-4 w-4 accent-amber-500"
              data-testid="cs-featured"
            />
            <span className="text-sm text-white inline-flex items-center gap-1.5">
              <Star size={14} className="text-amber-400" /> Feature this case study on the home page
            </span>
          </label>
        </div>

        <div className="card-dark rounded-3xl p-6 md:p-8 space-y-5">
          <label className="text-sm text-white font-medium block">Thumbnail image</label>
          <div className="flex items-center gap-4">
            <div className="h-28 w-48 rounded-xl overflow-hidden border border-white/10 bg-white/5 flex items-center justify-center">
              {form.thumbnail ? (
                <img src={resolveMedia(form.thumbnail)} alt="thumb" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs text-slate-500">No image</span>
              )}
            </div>
            <label className="cursor-pointer inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-4 h-10 text-sm text-white">
              {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
              <span>Upload image</span>
              <input type="file" accept="image/*" onChange={onThumbnail} className="hidden" data-testid="cs-thumbnail-input" />
            </label>
            {form.thumbnail && (
              <button type="button" onClick={() => setForm({ ...form, thumbnail: null })} className="text-xs text-slate-400 hover:text-red-300">
                Remove
              </button>
            )}
          </div>
          <p className="text-xs text-slate-500">Recommended: 1600×900 (16:9). PNG, JPG, WebP.</p>
        </div>

        <div className="flex items-center justify-end gap-3">
          <Link to="/admin/case-studies">
            <Button type="button" variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10 h-11 px-6">Cancel</Button>
          </Link>
          <Button type="submit" disabled={busy} data-testid="cs-save-btn" className="btn-gradient rounded-full h-11 px-8 text-white border-0">
            {busy ? 'Saving...' : (isEdit ? 'Save changes' : 'Create case study')}
          </Button>
        </div>
        </>
        )}
      </form>
    </div>
  );
};

export default AdminCaseStudyFormPage;
